import { ArrowUpRight, MapPin } from "lucide-react";
import { PROJECTS } from "../data/content";
import { TopoLines, Reveal, SectionHeader } from "./ui-bits";

export default function Projects() {
  return (
    <section id="projects" data-testid="waypoints-section" className="relative scroll-mt-20 py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <SectionHeader
          index="06"
          station="Projects"
          id="DISPATCH // CLIENT EXPEDITIONS"
          title={<>Field Dispatches<br /><span className="text-ember">My Projects</span></>}
          sub="Real websites shipped for clients across three countries — each one designed from scratch, every graphic and every word created along the way."
        />

        <div className="grid grid-cols-1 gap-6 md:gap-8 lg:grid-cols-2">
          {PROJECTS.map((p, i) => (
            <Reveal key={p.id} delay={(i % 2) * 0.08}>
              <article
                data-testid={`project-card-${p.id}`}
                className="group relative overflow-hidden border border-white/10 bg-ink transition-colors duration-300 hover:border-ember/60"
              >
                <TopoLines className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.05]" />
                <div className="relative">
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`project-shot-${p.id}`}
                    className="relative block aspect-[16/10] overflow-hidden"
                    aria-label={`Open ${p.title} live site`}
                  >
                    <img
                      src={p.img}
                      alt={`${p.title} — website homepage`}
                      loading="lazy"
                      className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-night/80 via-transparent to-night/10" />
                    <span className="absolute left-4 top-4 flex items-center gap-2 border border-white/25 bg-night/70 px-2.5 py-1.5 font-mono text-[9px] font-bold tracking-[0.2em] text-cream backdrop-blur-sm">
                      <span className="h-1.5 w-1.5 rounded-full bg-ember" /> {p.wp}
                    </span>
                    <span className="absolute bottom-4 right-4 flex translate-y-2 items-center gap-1.5 border border-ember/60 bg-night/80 px-3 py-1.5 font-mono text-[9px] font-bold tracking-[0.2em] text-ember opacity-0 backdrop-blur-sm transition-all duration-400 group-hover:translate-y-0 group-hover:opacity-100">
                      OPEN LIVE SITE <ArrowUpRight className="h-3.5 w-3.5" />
                    </span>
                  </a>

                  <div className="relative p-5 md:p-6">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h3 className="font-display text-xl font-black uppercase tracking-wide text-cream transition-colors duration-300 group-hover:text-ember md:text-2xl">
                        {p.title}
                      </h3>
                      <span className="flex items-center gap-1.5 font-mono text-[9px] tracking-[0.18em] text-slate-400">
                        <MapPin className="h-3 w-3 text-ember" /> {p.coords}
                      </span>
                    </div>
                    <p className="mt-1 font-mono text-[10px] tracking-[0.2em] text-ember">{p.cat}</p>
                    <p className="mt-3 text-sm leading-relaxed text-slate-300">{p.summary}</p>

                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {p.scope.map((t) => (
                        <span key={t} className="border border-white/15 px-2 py-1 font-mono text-[9px] tracking-[0.15em] text-slate-300">{t}</span>
                      ))}
                    </div>

                    <div className="mt-5 flex items-center justify-between gap-3 border-t border-dashed border-white/10 pt-4">
                      <span className="truncate font-mono text-[10px] tracking-[0.15em] text-cream/60">{p.domain}</span>
                      <a
                        href={p.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`project-live-link-${p.id}`}
                        className="flex shrink-0 items-center gap-1.5 border border-cream/20 px-3 py-2 font-mono text-[9px] font-bold tracking-[0.2em] text-cream transition-colors hover:border-ember hover:text-ember"
                      >
                        VISIT LIVE SITE <ArrowUpRight className="h-3.5 w-3.5" />
                      </a>
                    </div>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border border-white/10 bg-ink/60 px-5 py-4" data-testid="waypoints-footer">
            <span className="font-mono text-[9px] tracking-[0.25em] text-cream/50">FOUR DISPATCHES LOGGED — MORE WAYPOINTS BEING CHARTED</span>
            <span className="flex items-center gap-2 font-mono text-[9px] font-bold tracking-[0.25em] text-ember">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-ember" /> WP-05 IN PREPARATION
            </span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
